import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { trace, metrics, type Tracer, type Meter } from '@opentelemetry/api';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { storeToRefs } from 'pinia';
import { watch, ref, computed } from 'vue';
import { appSettings, getApiHost } from '@/appSettings';

type UseOpentelemetry = {
    init: () => void;
    getTracer: (name?: string) => Tracer;
    getMeter: (name?: string) => Meter;
};

let tracerProvider: WebTracerProvider | null = null;
let meterProvider: MeterProvider | null = null;
let isInitialized = false;

const SERVICE_NAME = 'sysarb-vue-client';

/**
 * Provides OpenTelemetry tracing and metrics functionality.
 * Initializes telemetry when user logs in and shuts down when they log out.
 */
export const useOpentelemetry = (): UseOpentelemetry => {
    const loginStore = useLoginStore();
    const { isLoggedIn, userData, selectedInstance, instance } = storeToRefs(loginStore);

    const loginUser = computed(() => userData.value?.loginUser ?? null);
    const isEnabled = ref(false);

    function getHeaders(): Record<string, string> {
        return {
            'Content-Type': 'application/json',
            'x-subdomain': instance.value ?? '',
        };
    }

    function createTracerProvider(): WebTracerProvider {
        const resource = resourceFromAttributes({
            [ATTR_SERVICE_NAME]: SERVICE_NAME,
            [ATTR_SERVICE_VERSION]: import.meta.env.VITE_APP_VERSION ?? '0.0.0',
        });

        const collectorUrl = `https://${getApiHost()}/opentelemetry-service/v1/traces`;

        const exporter = new OTLPTraceExporter({
            url: collectorUrl,
            headers: getHeaders(),
        });

        const provider = new WebTracerProvider({
            resource,
            spanProcessors: [new BatchSpanProcessor(exporter)],
        });

        return provider;
    }

    function createMeterProvider(): MeterProvider {
        const resource = resourceFromAttributes({
            [ATTR_SERVICE_NAME]: SERVICE_NAME,
            [ATTR_SERVICE_VERSION]: import.meta.env.VITE_APP_VERSION ?? '0.0.0',
        });

        const collectorUrl = `https://${getApiHost()}/opentelemetry-service/v1/metrics`;

        const exporter = new OTLPMetricExporter({
            url: collectorUrl,
            headers: getHeaders(),
        });

        const provider = new MeterProvider({
            resource,
            readers: [
                new PeriodicExportingMetricReader({
                    exporter,
                    exportIntervalMillis: 60000, // Export every 60 seconds
                }),
            ],
        });

        return provider;
    }

    function startTelemetry(): void {
        if (tracerProvider != null) return;

        // Initialize tracing
        tracerProvider = createTracerProvider();
        tracerProvider.register();

        // Initialize metrics
        meterProvider = createMeterProvider();
        metrics.setGlobalMeterProvider(meterProvider);

        isEnabled.value = true;
        console.info('OpenTelemetry telemetry started');
    }

    function stopTelemetry(): void {
        // Shutdown tracing
        if (tracerProvider != null) {
            tracerProvider.shutdown()
                .then(() => {
                    console.info('OpenTelemetry tracing stopped');
                })
                .catch((error: unknown) => {
                    console.error('Error shutting down OpenTelemetry tracing:', error);
                });
            tracerProvider = null;
        }

        // Shutdown metrics
        if (meterProvider != null) {
            meterProvider.shutdown()
                .then(() => {
                    console.info('OpenTelemetry metrics stopped');
                })
                .catch((error: unknown) => {
                    console.error('Error shutting down OpenTelemetry metrics:', error);
                });
            meterProvider = null;
        }

        isEnabled.value = false;
    }

    function init(): void {
        if (isInitialized) return;

        if (appSettings.enableOpentelemetry !== true) {
            console.info('OpenTelemetry is disabled');
            return;
        }

        isInitialized = true;

        // Watch login state to enable/disable telemetry
        watch(
            () => isLoggedIn.value,
            (loggedIn) => {
                if (loggedIn) {
                    startTelemetry();
                } else {
                    stopTelemetry();
                }
            },
            { immediate: true }
        );

        // Watch user and instance changes to update span attributes
        watch(
            [loginUser, selectedInstance],
            () => {
                const user = loginUser.value;
                const currentInstance = selectedInstance.value;

                if (tracerProvider == null || user == null) return;

                // Add user context to active spans via context propagation
                const tracer = trace.getTracer(SERVICE_NAME);
                const span = tracer.startSpan('user-context-update');
                span.setAttribute('user.id', user.Id);
                span.setAttribute('user.email', user.Email ?? 'unknown');
                if (currentInstance != null) {
                    span.setAttribute('instance.subdomain', currentInstance.SubDomain);
                    span.setAttribute('instance.name', currentInstance.Name ?? 'unknown');
                }
                span.end();
            },
            { immediate: true }
        );
    }

    function getTracer(name = SERVICE_NAME): Tracer {
        return trace.getTracer(name);
    }

    function getMeter(name = SERVICE_NAME): Meter {
        return metrics.getMeter(name);
    }

    return {
        init,
        getTracer,
        getMeter,
    };
};
